"use client";

// SiteLogsTab.js — Operations › Site Logs
// Web review of the walkthroughs the foreman records on the mobile app:
// list + unit-aware search, per-log report with photos, deficiency tracking,
// in-place editing (no modal), combining sections from other reports, PDF.
//
// Backend: mount the same router for the web as well (use your web auth middleware):
//   app.use("/api/site-logs", yourWebAuthMiddleware, require("./routes/siteLogs"));
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";

const API = "https://erp-production-72da01c8e651.herokuapp.com/api/site-logs";

const TYPES = [
  { key: "observation", label: "Observation", color: "#3b82f6" },
  { key: "deficiency", label: "Deficiency", color: "#ef4444" },
  { key: "safety", label: "Safety", color: "#eab308" },
  { key: "progress", label: "Progress", color: "#10b981" },
  { key: "other", label: "Other", color: "#9ca3af" },
];
const typeMeta = (k) => TYPES.find((t) => t.key === k) || TYPES[0];

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "X-User-ID": localStorage.getItem("userId") || "",
});
const http = axios.create({ baseURL: API });
http.interceptors.request.use((cfg) => {
  cfg.headers = { ...(cfg.headers || {}), ...authHeaders() };
  return cfg;
});
const errMsg = (e) =>
  e?.response?.data?.message || e?.message || "Something went wrong";

const fmtClock = (ms) => {
  const s = Math.floor(ms / 1000);
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};

// ─── search helpers (same rules as the server and the mobile app) ───
// "unit 204", "suite 204", "#204" and "204" all search for 204; numbers match whole numbers only.
const FILLER = new Set([
  "unit",
  "suite",
  "apt",
  "apartment",
  "room",
  "rm",
  "no",
  "number",
]);
const escRx = (x) => x.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
function searchTerms(q) {
  const all = String(q || "")
    .toLowerCase()
    .split(/[\s,]+/)
    .map((x) => x.replace(/^#/, ""))
    .filter(Boolean);
  const stripped = all.filter((x) => !FILLER.has(x));
  return stripped.length ? stripped : all;
}
function termRegex(term, flags = "i") {
  const e = escRx(term);
  try {
    return /^\d+[a-z]?$/i.test(term)
      ? new RegExp(`(?<![0-9])${e}`, flags)
      : new RegExp(e, flags);
  } catch {
    return new RegExp(e, flags);
  }
}
const WEIGHTS = {
  unit: 100,
  title: 20,
  tags: 20,
  area: 10,
  notes: 5,
  transcript: 3,
};
function scoreSection(s, terms) {
  let score = 0;
  let matched = true;
  for (const t of terms) {
    const rx = termRegex(t);
    let hit = false;
    for (const [f, w] of Object.entries(WEIGHTS)) {
      const v = f === "tags" ? (s.tags || []).join(" ") : s[f];
      if (v && rx.test(v)) {
        score += w;
        hit = true;
      }
    }
    if (!hit) matched = false;
    if ((s.unit || "").trim().toLowerCase() === t) score += 50;
  }
  return { score, matched };
}

function Highlight({ text, terms, className = "" }) {
  const str = String(text || "");
  if (!terms?.length || !str) return <span className={className}>{str}</span>;
  const rx = new RegExp(terms.map((t) => termRegex(t).source).join("|"), "gi");
  const parts = [];
  let last = 0;
  let m;
  while ((m = rx.exec(str))) {
    if (m[0] === "") {
      rx.lastIndex++;
      continue;
    }
    if (m.index > last) parts.push({ t: str.slice(last, m.index) });
    parts.push({ t: m[0], hit: true });
    last = m.index + m[0].length;
  }
  if (last < str.length) parts.push({ t: str.slice(last) });
  return (
    <span className={className}>
      {parts.map((p, i) =>
        p.hit ? (
          <mark key={i} className="bg-orange-500/40 text-white rounded px-0.5">
            {p.t}
          </mark>
        ) : (
          <span key={i}>{p.t}</span>
        ),
      )}
    </span>
  );
}

// <img> can't send the auth header, so fetch the photo as a blob
function AuthImage({ fileId, className, onClick, alt = "" }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    let alive = true;
    http
      .get(`/photos/${fileId}`, { responseType: "blob" })
      .then((r) => {
        if (!alive) return;
        url = URL.createObjectURL(r.data);
        setSrc(url);
      })
      .catch(() => {});
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [fileId]);
  if (!src) return <div className={`${className} bg-gray-700 animate-pulse`} />;
  return <img src={src} alt={alt} className={className} onClick={onClick} />;
}

const Pill = ({ children, color, className = "" }) => (
  <span
    className={`inline-block text-xs font-semibold px-2.5 py-0.5 rounded-full capitalize ${className}`}
    style={
      color
        ? { backgroundColor: color + "26", color }
        : { backgroundColor: "#374151", color: "#d1d5db" }
    }
  >
    {children}
  </span>
);

function useAssignees() {
  const [users, setUsers] = useState([]);
  useEffect(() => {
    http
      .get("/assignees")
      .then((r) => setUsers(r.data.users || []))
      .catch(() => {});
  }, []);
  return users;
}

function AssigneePicker({
  value,
  users,
  onChange,
  emptyLabel = "Unassigned",
  clearLabel = "Unassigned",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const needle = q.trim().toLowerCase();
  const list = needle
    ? users.filter((u) =>
        `${u.displayName} ${u.email} ${u.role}`.toLowerCase().includes(needle),
      )
    : users;
  const pick = (id) => {
    setOpen(false);
    setQ("");
    onChange(id);
  };

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`text-sm px-3 py-1 rounded-md border bg-gray-800 hover:border-orange-400 ${
          value
            ? "border-gray-600 text-orange-300"
            : "border-gray-600 text-gray-400 italic"
        }`}
      >
        {value?.displayName || emptyLabel} ▾
      </button>
      {open && (
        <div className="absolute left-0 z-40 mt-1 w-64 bg-gray-800 border border-gray-600 rounded-lg shadow-xl">
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search people…"
            className="w-full bg-gray-900 border-b border-gray-600 outline-none rounded-t-lg px-3 py-2 text-sm text-white placeholder-gray-500"
          />
          <div className="max-h-60 overflow-y-auto">
            <button
              type="button"
              onClick={() => pick(null)}
              className="w-full text-left px-3 py-2 text-sm italic text-gray-400 hover:bg-gray-700"
            >
              {clearLabel}
            </button>
            {list.map((u) => (
              <button
                type="button"
                key={u._id}
                onClick={() => pick(u._id)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-700 flex justify-between gap-2 ${
                  value?._id === u._id ? "text-orange-300" : "text-gray-100"
                }`}
              >
                <span className="truncate">{u.displayName}</span>
                <span className="text-xs text-gray-500 capitalize shrink-0">
                  {String(u.role).replace("_", " ")}
                </span>
              </button>
            ))}
            {list.length === 0 && (
              <div className="px-3 py-3 text-sm text-gray-500">No matches</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SiteLogsTab({ project }) {
  const projectId = project?._id;
  const [openLogId, setOpenLogId] = useState(null);
  const [focusSectionId, setFocusSectionId] = useState(null);
  const [focusQ, setFocusQ] = useState("");

  useEffect(() => {
    setOpenLogId(null);
  }, [projectId]);

  if (!projectId) return null;
  return openLogId ? (
    <LogReport
      key={openLogId}
      logId={openLogId}
      initialSectionId={focusSectionId}
      initialQ={focusQ}
      onBack={() => {
        setOpenLogId(null);
        setFocusSectionId(null);
        setFocusQ("");
      }}
    />
  ) : (
    <LogList
      project={project}
      onOpen={(id, sectionId, q) => {
        setFocusSectionId(sectionId || null);
        setFocusQ(q || "");
        setOpenLogId(id);
      }}
    />
  );
}

// ═════════════════════════ List + search ═════════════════════════
function LogList({ project, onOpen }) {
  const projectId = project._id;
  const [logs, setLogs] = useState([]);
  const [tags, setTags] = useState([]);
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [tag, setTag] = useState(null);
  const [type, setType] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [assignee, setAssignee] = useState(null); // null | "me" | "unassigned" | userId
  const users = useAssignees();

  useEffect(() => {
    const t = setTimeout(() => setDq(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    try {
      const params = { projectId };
      if (dq) params.q = dq;
      if (tag) params.tag = tag;
      if (assignee) params.assignedTo = assignee;
      if (type) params.type = type;
      if (onlyOpen) params.status = "open";
      const [list, tagRes] = await Promise.all([
        http.get("/", { params }),
        http.get("/tags", { params: { projectId } }),
      ]);
      setLogs(list.data.logs);
      setTags(tagRes.data.tags || []);
      setError(null);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [projectId, dq, tag, type, onlyOpen, assignee]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = !!(dq || tag || type || onlyOpen || assignee);
  const terms = useMemo(() => searchTerms(dq), [dq]);

  const createLog = async () => {
    try {
      setCreating(true);
      const { data } = await http.post("/", {
        projectId,
        projectName: project.projectName,
      });
      onOpen(data.log._id);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setCreating(false);
    }
  };

  const totalOpen = logs.reduce((n, l) => n + (l.openDeficiencies || 0), 0);

  return (
    <div className="text-gray-200">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-semibold text-white">Site Logs</h2>
          <p className="text-sm text-gray-600">
            Walkthroughs recorded by the foreman on the mobile app
            {!filtered && logs.length > 0 && (
              <>
                {" "}
                · {logs.length} log{logs.length === 1 ? "" : "s"}
                {totalOpen > 0 && (
                  <span className="text-red-400 font-semibold">
                    {" "}
                    · {totalOpen} open deficiencies
                  </span>
                )}
              </>
            )}
          </p>
        </div>
        <button
          onClick={createLog}
          disabled={creating}
          className="px-4 py-2 rounded-md bg-orange-400 hover:bg-orange-500 text-white text-sm font-semibold disabled:opacity-50"
        >
          {creating ? "Creating…" : "+ New Site Log"}
        </button>
      </div>

      {/* search */}
      <div className="relative mb-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search unit # (e.g. 204), notes, transcripts, tags…"
          className="w-full bg-gray-800 border border-gray-600 focus:border-orange-400 outline-none rounded-md pl-4 pr-10 py-2.5 text-sm text-white placeholder-gray-500"
        />
        {q && (
          <button
            onClick={() => setQ("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
          >
            ✕
          </button>
        )}
      </div>

      {/* filters */}
      <div className="flex flex-wrap gap-2 mb-2">
        <Chip
          active={onlyOpen}
          color="#ef4444"
          onClick={() => setOnlyOpen((v) => !v)}
        >
          Open only
        </Chip>
        {TYPES.map((t) => (
          <Chip
            key={t.key}
            active={type === t.key}
            color={t.color}
            onClick={() => setType(type === t.key ? null : t.key)}
          >
            {t.label}
          </Chip>
        ))}
        <select
          value={assignee || ""}
          onChange={(e) => setAssignee(e.target.value || null)}
          className="text-xs font-semibold px-3 py-1.5 rounded-full border border-gray-600 bg-gray-800 text-gray-200 outline-none focus:border-orange-400"
        >
          <option value="">All assignees</option>
          <option value="me">Assigned to me</option>
          <option value="unassigned">Unassigned</option>
          {users.map((u) => (
            <option key={u._id} value={u._id}>
              {u.displayName}
            </option>
          ))}
        </select>
      </div>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {tags.map((t) => (
            <Chip
              key={t.tag}
              active={tag === t.tag}
              color="#f97316"
              onClick={() => setTag(tag === t.tag ? null : t.tag)}
            >
              #{t.tag} {t.count}
            </Chip>
          ))}
        </div>
      )}

      {error && <div className="text-red-400 text-sm mb-3">{error}</div>}
      {loading ? (
        <div className="text-center py-10 text-gray-400">Loading…</div>
      ) : logs.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          {filtered
            ? "No logs match your search."
            : "No site logs yet for this project."}
        </div>
      ) : (
        <div className="grid gap-3">
          {logs.map((l) => (
            <div
              key={l._id}
              className="bg-gray-800 border border-gray-700 hover:border-orange-400/60 rounded-xl p-4 cursor-pointer transition"
              onClick={() => onOpen(l._id, null, dq)}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-white truncate">
                    {l.title}
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    {new Date(l.logDate).toLocaleDateString("en-CA")}
                    {" · "}
                    <span
                      className={
                        l.assignedTo
                          ? "text-orange-300"
                          : "text-gray-500 italic"
                      }
                    >
                      {l.assignedTo ? l.assignedTo.displayName : "Unassigned"}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-300 shrink-0">
                  <span>{l.sectionCount} sections</span>
                  <span>{l.photoCount} photos</span>
                  {l.openDeficiencies > 0 && (
                    <Pill color="#ef4444">{l.openDeficiencies} open</Pill>
                  )}
                </div>
              </div>

              {!filtered && l.units?.length > 0 && (
                <div className="text-xs text-gray-500 mt-2 truncate">
                  Units: {l.units.join(", ")}
                </div>
              )}

              {filtered && l.matchedSections?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-700 space-y-1.5">
                  <div className="text-[11px] font-bold tracking-wider text-orange-400">
                    {l.matchedCount} MATCHING SECTION
                    {l.matchedCount === 1 ? "" : "S"}
                  </div>
                  {l.matchedSections.map((m) => {
                    const tm = typeMeta(m.type);
                    return (
                      <div
                        key={m._id}
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpen(l._id, m._id, dq);
                        }}
                        className="flex gap-3 rounded-lg p-2 hover:bg-gray-700/60"
                      >
                        <span
                          className="w-2 h-2 rounded-full mt-2 shrink-0"
                          style={{ backgroundColor: tm.color }}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-1.5">
                            {m.unit && (
                              <span className="text-xs font-extrabold px-2 py-0.5 rounded-md bg-orange-500/25 text-orange-300">
                                <Highlight
                                  text={`Unit ${m.unit}`}
                                  terms={terms}
                                />
                              </span>
                            )}
                            <Pill color={tm.color}>{tm.label}</Pill>
                            {m.type === "deficiency" && (
                              <Pill
                                color={
                                  m.status === "open" ? "#ef4444" : "#10b981"
                                }
                              >
                                {m.status}
                              </Pill>
                            )}
                            {m.photoCount > 0 && (
                              <span className="text-[11px] text-gray-500">
                                {m.photoCount} photos
                              </span>
                            )}
                          </div>
                          <div className="text-sm font-medium text-gray-100 mt-0.5 truncate">
                            <Highlight
                              text={`${m.title || "Untitled"}${m.area ? "  ·  " + m.area : ""}`}
                              terms={terms}
                            />
                          </div>
                          {m.snippet && (
                            <div className="text-xs text-gray-400 line-clamp-2">
                              <Highlight text={m.snippet} terms={terms} />
                            </div>
                          )}
                          {m.tags?.length > 0 && (
                            <div className="text-xs text-orange-400 mt-0.5">
                              {m.tags.map((t) => `#${t}`).join("  ")}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {l.matchedCount > l.matchedSections.length && (
                    <div className="text-xs italic text-gray-500">
                      +{l.matchedCount - l.matchedSections.length} more in this
                      log
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const Chip = ({ active, color, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="text-xs font-semibold px-3 py-1.5 rounded-full border transition"
    style={
      active
        ? { backgroundColor: color + "28", borderColor: color, color }
        : {
            borderColor: "#4b5563",
            color: "#d1d5db",
            backgroundColor: "#1f2937",
          }
    }
  >
    {children}
  </button>
);

const inputCls =
  "w-full bg-gray-900 border border-gray-600 focus:border-orange-400 outline-none rounded-md px-3 py-2 text-sm text-white placeholder-gray-500";
const labelCls =
  "block text-[11px] font-bold tracking-wider text-orange-400 mb-1";

// ═════════════════════════ One log: report ═════════════════════════
function LogReport({ logId, initialSectionId, initialQ, onBack }) {
  const [log, setLog] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState(initialQ || "");
  const [type, setType] = useState(null);
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [unit, setUnit] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [viewer, setViewer] = useState(null); // fileId
  const [pdfBusy, setPdfBusy] = useState(false);
  const [focusId, setFocusId] = useState(initialSectionId);
  const [titleDraft, setTitleDraft] = useState("");
  const cardRefs = useRef({});

  const users = useAssignees();

  const load = useCallback(async () => {
    try {
      const { data } = await http.get(`/${logId}`);
      setLog(data.log);
      setError(null);
    } catch (e) {
      setError(errMsg(e));
    }
  }, [logId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (log) setTitleDraft(log.title || "");
  }, [log?.title]); // eslint-disable-line

  const assignLog = async (userId) => {
    const prev = log.assignedTo;
    const user = users.find((u) => u._id === userId) || null;
    setLog((p) => ({ ...p, assignedTo: user }));
    try {
      await http.patch(`/${logId}`, { assignedTo: userId || null });
      load(); // re-read the saved, populated value
    } catch (e) {
      setLog((p) => ({ ...p, assignedTo: prev }));
      setError(errMsg(e));
    }
  };

  const assignSection = async (sid, userId) => {
    const prev = log.sections.find((s) => s._id === sid)?.assignedTo || null;
    const user = users.find((u) => u._id === userId) || null;
    const set = (v) =>
      setLog((p) => ({
        ...p,
        sections: p.sections.map((s) =>
          s._id === sid ? { ...s, assignedTo: v } : s,
        ),
      }));
    set(user);
    try {
      await http.patch(`/${logId}/sections/${sid}`, {
        assignedTo: userId || null,
      });
      load();
    } catch (e) {
      set(prev);
      setError(errMsg(e));
    }
  };

  const renameLog = async () => {
    const t = titleDraft.trim();
    if (!t || t === log.title) {
      setTitleDraft(log.title || "");
      return;
    }
    setLog((p) => ({ ...p, title: t }));
    try {
      await http.patch(`/${logId}`, { title: t });
    } catch (e) {
      setError(errMsg(e));
      load();
    }
  };

  // keep refreshing while the server is still transcribing
  const transcribing = (log?.recordings || []).some(
    (r) => r.status === "queued" || r.status === "transcribing",
  );
  useEffect(() => {
    if (!transcribing) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [transcribing, load]);

  useEffect(() => {
    if (!focusId || !log) return;
    const el = cardRefs.current[focusId];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [focusId, !!log]); // eslint-disable-line

  const terms = useMemo(() => searchTerms(q), [q]);

  const patchSection = async (sid, patch) => {
    setLog((p) => ({
      ...p,
      sections: p.sections.map((s) => (s._id === sid ? { ...s, ...patch } : s)),
    }));
    try {
      await http.patch(`/${logId}/sections/${sid}`, patch);
    } catch (e) {
      setError(errMsg(e));
      load();
    }
  };

  const deleteSection = async (s) => {
    if (!window.confirm("Delete this section and its photos?")) return;
    try {
      await http.delete(`/${logId}/sections/${s._id}`);
      setLog((p) => ({
        ...p,
        sections: p.sections.filter((x) => x._id !== s._id),
      }));
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const addBlankSection = async () => {
    try {
      await http.post(`/${logId}/sections`, {
        title: `Section ${(log?.sections?.length || 0) + 1}`,
      });
      await load();
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const uploadPhotos = async (s, files) => {
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("photo", f);
        const { data } = await http.post(
          `/${logId}/sections/${s._id}/photos`,
          fd,
        );
        setLog((p) => ({
          ...p,
          sections: p.sections.map((x) =>
            x._id === s._id ? { ...x, photos: [...x.photos, data.photo] } : x,
          ),
        }));
      } catch (e) {
        setError(errMsg(e));
      }
    }
  };

  const deletePhoto = async (s, p) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await http.delete(`/${logId}/sections/${s._id}/photos/${p._id}`);
      setLog((prev) => ({
        ...prev,
        sections: prev.sections.map((x) =>
          x._id === s._id
            ? { ...x, photos: x.photos.filter((y) => y._id !== p._id) }
            : x,
        ),
      }));
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const downloadPdf = async () => {
    try {
      setPdfBusy(true);
      const r = await http.get(`/${logId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(log?.title || "site-log").replace(/[^\w\-]+/g, "_")}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setPdfBusy(false);
    }
  };

  const retryTranscription = async (rec) => {
    try {
      await http.post(`/${logId}/recordings/${rec._id}/retry`);
      load();
    } catch (e) {
      setError(errMsg(e));
    }
  };

  if (error && !log) {
    return (
      <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 text-gray-200">
        <button onClick={onBack} className="text-orange-400 text-sm mb-3">
          ← Back to site logs
        </button>
        <div className="text-red-400">{error}</div>
      </div>
    );
  }
  if (!log)
    return <div className="text-center py-10 text-gray-400">Loading…</div>;

  const secs = log.sections;
  const photoTotal = secs.reduce((n, s) => n + s.photos.length, 0);
  const defs = secs.filter((s) => s.type === "deficiency");
  const openDef = defs.filter((s) => s.status === "open").length;
  const unitMap = new Map();
  secs.forEach((s) => {
    const u = (s.unit || "").trim();
    if (u) unitMap.set(u, (unitMap.get(u) || 0) + 1);
  });
  const units = [...unitMap.entries()].sort((a, b) =>
    a[0].localeCompare(b[0], undefined, { numeric: true }),
  );
  const filtered = !!(terms.length || type || onlyOpen || unit);

  let visible = secs
    .map((s, i) => ({ s, i, score: 0 }))
    .filter(({ s }) => {
      if (type && s.type !== type) return false;
      if (onlyOpen && !(s.type === "deficiency" && s.status === "open"))
        return false;
      if (unit && (s.unit || "").trim() !== unit) return false;
      return true;
    });
  if (terms.length) {
    visible = visible
      .map((v) => ({ ...v, ...scoreSection(v.s, terms) }))
      .filter((v) => v.matched)
      .sort((a, b) => b.score - a.score || a.i - b.i);
  }

  const failed = (log.recordings || []).find((r) => r.status === "failed");

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-2xl p-4 md:p-6 text-gray-200 shadow-xl">
      {/* header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="min-w-0 flex-1">
          <button
            onClick={onBack}
            className="block text-orange-400 hover:text-orange-300 text-sm mb-2"
          >
            ← Back to site logs
          </button>
          {editMode ? (
            <input
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={renameLog}
              onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
              className={`${inputCls} text-xl font-semibold py-2.5`}
              placeholder="Report title"
            />
          ) : (
            <h2 className="text-xl font-semibold text-white truncate">
              {log.title}
            </h2>
          )}
          <p className="text-sm text-gray-400 mt-1">
            {new Date(log.logDate).toLocaleDateString("en-CA", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-[10px] font-extrabold tracking-widest text-gray-500">
              ASSIGNED TO
            </span>
            <AssigneePicker
              value={log.assignedTo}
              users={users}
              onChange={assignLog}
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setEditMode((v) => !v)}
            className={`px-4 py-2 rounded-md text-sm font-semibold border ${
              editMode
                ? "bg-orange-400 border-orange-400 text-white hover:bg-orange-500"
                : "bg-gray-800 border-orange-400 text-orange-300 hover:bg-gray-700"
            }`}
          >
            {editMode ? "✓ Done editing" : "✎ Edit report"}
          </button>
          <button
            onClick={downloadPdf}
            disabled={pdfBusy}
            className="px-4 py-2 rounded-md bg-orange-400 hover:bg-orange-500 text-white text-sm font-semibold disabled:opacity-50"
          >
            {pdfBusy ? "Preparing…" : "Download PDF"}
          </button>
          <button
            onClick={async () => {
              if (
                !window.confirm(
                  "Delete this entire site log and all its photos?",
                )
              )
                return;
              try {
                await http.delete(`/${logId}`);
                onBack();
              } catch (e) {
                setError(errMsg(e));
              }
            }}
            className="px-3 py-2 rounded-md bg-gray-700 hover:bg-red-600 text-gray-200 text-sm"
          >
            Delete
          </button>
        </div>
      </div>

      {error && <div className="text-red-400 text-sm mb-3">{error}</div>}
      {transcribing && (
        <div className="mb-3 px-4 py-2.5 rounded-lg border border-orange-400/50 bg-orange-400/10 text-sm text-orange-200">
          Transcribing the recording… notes will appear in each section.
        </div>
      )}
      {failed && (
        <div className="mb-3 px-4 py-2.5 rounded-lg border border-red-500/60 bg-red-500/10 text-sm text-red-200 flex justify-between items-center">
          <span>
            Transcription failed{failed.error ? `: ${failed.error}` : ""}
          </span>
          <button
            onClick={() => retryTranscription(failed)}
            className="text-orange-300 font-semibold"
          >
            Retry
          </button>
        </div>
      )}

      {editMode && (
        <div className="mb-4 px-4 py-2.5 rounded-lg border-l-4 border-orange-400 bg-orange-400/10 text-sm text-orange-100">
          <span className="font-semibold text-orange-300">Editing</span> —
          changes save automatically as you leave each field. Click a photo to
          enlarge it.
        </div>
      )}

      {/* tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Tile label="Sections" value={secs.length} />
        <Tile label="Photos" value={photoTotal} />
        <Tile
          label="Open deficiencies"
          value={openDef}
          color={openDef ? "#ef4444" : "#10b981"}
          active={onlyOpen}
          onClick={openDef ? () => setOnlyOpen((v) => !v) : undefined}
        />
        <Tile label="Resolved" value={defs.length - openDef} color="#10b981" />
      </div>

      {/* find + filters */}
      <div className="relative mb-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Find in this log — unit #, words, tags…"
          className="w-full bg-gray-800 border border-gray-600 focus:border-orange-400 outline-none rounded-md pl-4 pr-10 py-2.5 text-sm text-white placeholder-gray-500"
        />
        {q && (
          <button
            onClick={() => setQ("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
          >
            ✕
          </button>
        )}
      </div>
      {units.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className="text-[10px] font-extrabold tracking-widest text-gray-500">
            UNITS
          </span>
          {units.map(([u, n]) => (
            <Chip
              key={u}
              active={unit === u}
              color="#f97316"
              onClick={() => setUnit(unit === u ? null : u)}
            >
              {u}
              {n > 1 ? ` ·${n}` : ""}
            </Chip>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="text-[10px] font-extrabold tracking-widest text-gray-500">
          TYPE
        </span>
        {TYPES.filter((t) => secs.some((s) => s.type === t.key)).map((t) => (
          <Chip
            key={t.key}
            active={type === t.key}
            color={t.color}
            onClick={() => setType(type === t.key ? null : t.key)}
          >
            {t.label} ·{secs.filter((s) => s.type === t.key).length}
          </Chip>
        ))}
      </div>
      {filtered && (
        <div className="flex items-center justify-between text-sm mb-3">
          <span className="text-gray-400">
            Showing {visible.length} of {secs.length} section
            {secs.length === 1 ? "" : "s"}
          </span>
          <button
            className="text-orange-400 font-semibold"
            onClick={() => {
              setQ("");
              setType(null);
              setOnlyOpen(false);
              setUnit(null);
            }}
          >
            Clear
          </button>
        </div>
      )}

      {/* combine reports */}
      {editMode && (
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowImport((v) => !v)}
              className="px-4 py-2 rounded-md border border-orange-400 text-orange-300 hover:bg-orange-400/10 text-sm font-semibold"
            >
              {showImport ? "Close" : "⇄ Add sections from another report"}
            </button>
            <button
              onClick={addBlankSection}
              className="px-4 py-2 rounded-md border border-gray-600 text-gray-300 hover:border-orange-400 text-sm font-semibold"
            >
              + Blank section
            </button>
          </div>
          {showImport && (
            <ImportSections
              logId={logId}
              projectId={log.projectId}
              onMoved={load}
              onError={setError}
            />
          )}
        </div>
      )}

      {visible.length === 0 && (
        <div className="text-center text-gray-500 py-12">
          {secs.length === 0
            ? "No sections in this log yet."
            : "No sections match. Try just the unit number, e.g. 204."}
        </div>
      )}

      {/* sections */}
      <div className="space-y-4">
        {visible.map(({ s, i }) => (
          <SectionCard
            key={s._id}
            s={s}
            index={i}
            log={log}
            users={users}
            terms={terms}
            edit={editMode}
            transcribing={transcribing}
            expanded={expanded}
            setExpanded={setExpanded}
            focused={focusId === s._id}
            cardRef={(el) => (cardRefs.current[s._id] = el)}
            onPatch={(patch) => patchSection(s._id, patch)}
            onAssign={(id) => assignSection(s._id, id)}
            onDelete={() => deleteSection(s)}
            onUpload={(files) => uploadPhotos(s, files)}
            onDeletePhoto={(p) => deletePhoto(s, p)}
            onViewPhoto={setViewer}
          />
        ))}
      </div>

      {/* photo viewer */}
      {viewer && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6"
          onClick={() => setViewer(null)}
        >
          <AuthImage
            fileId={viewer}
            className="max-h-full max-w-full object-contain rounded"
          />
          <button className="absolute top-5 right-6 text-white text-2xl">
            ✕
          </button>
        </div>
      )}
    </div>
  );
}

// ═════════════════════════ Section card (read + inline edit) ═════════════════════════
function SectionCard({
  s,
  index,
  log,
  users,
  terms,
  edit,
  transcribing,
  expanded,
  setExpanded,
  focused,
  cardRef,
  onPatch,
  onAssign,
  onDelete,
  onUpload,
  onDeletePhoto,
  onViewPhoto,
}) {
  const m = typeMeta(s.type);
  const fileRef = useRef(null);

  // local drafts — saved on blur so typing never fights the 5s transcription refresh
  const [title, setTitle] = useState(s.title || "");
  const [unitV, setUnitV] = useState(s.unit || "");
  const [area, setArea] = useState(s.area || "");
  const [notes, setNotes] = useState(s.notes || "");
  const [tagDraft, setTagDraft] = useState("");
  const typing = useRef(false);

  useEffect(() => {
    if (typing.current) return;
    setTitle(s.title || "");
    setUnitV(s.unit || "");
    setArea(s.area || "");
    setNotes(s.notes || "");
  }, [s.title, s.unit, s.area, s.notes]);

  const commit = (field, value) => {
    typing.current = false;
    const next = typeof value === "string" ? value.trim() : value;
    if ((s[field] || "") !== next) onPatch({ [field]: next });
  };

  const addTag = (raw) => {
    const t = String(raw).trim().toLowerCase().replace(/^#/, "");
    setTagDraft("");
    if (t && !(s.tags || []).includes(t))
      onPatch({ tags: [...(s.tags || []), t] });
  };
  const removeTag = (t) =>
    onPatch({ tags: (s.tags || []).filter((x) => x !== t) });

  const notesTrim = (s.notes || "").trim();
  const transcript = (s.transcript || "").trim();
  const long = notesTrim.length > 400;

  return (
    <div
      ref={cardRef}
      className={`bg-gray-800 rounded-xl p-5 border scroll-mt-4 ${
        focused ? "border-orange-400" : "border-gray-700"
      } ${edit ? "border-l-4 border-l-orange-400" : ""}`}
    >
      {/* title row */}
      <div className="flex items-start gap-3">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center font-extrabold text-sm shrink-0"
          style={{ backgroundColor: m.color + "30", color: m.color }}
        >
          {index + 1}
        </div>
        <div className="min-w-0 flex-1">
          {edit ? (
            <input
              className={`${inputCls} text-base font-semibold`}
              value={title}
              placeholder="Section title"
              onFocus={() => (typing.current = true)}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() => commit("title", title)}
            />
          ) : (
            <div className="text-lg font-semibold text-white">
              <Highlight text={s.title || "Untitled section"} terms={terms} />
            </div>
          )}
          {s.recordingId && typeof s.startMs === "number" && (
            <div className="text-xs text-gray-500 mt-0.5">
              Walkthrough {fmtClock(s.startMs)}
            </div>
          )}
        </div>
        {edit && (
          <button
            onClick={onDelete}
            className="text-sm text-red-400 hover:text-red-300 font-semibold shrink-0"
          >
            Delete
          </button>
        )}
      </div>

      {/* unit / area / type */}
      {edit ? (
        <>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <label className={labelCls}>UNIT</label>
              <input
                className={inputCls}
                value={unitV}
                placeholder="e.g. 204"
                onFocus={() => (typing.current = true)}
                onChange={(e) => setUnitV(e.target.value)}
                onBlur={() => commit("unit", unitV)}
              />
            </div>
            <div>
              <label className={labelCls}>AREA</label>
              <input
                className={inputCls}
                value={area}
                placeholder="e.g. Bedroom 2"
                onFocus={() => (typing.current = true)}
                onChange={(e) => setArea(e.target.value)}
                onBlur={() => commit("area", area)}
              />
            </div>
          </div>
          <label className={`${labelCls} mt-3`}>TYPE</label>
          <div className="flex flex-wrap gap-2">
            {TYPES.map((t) => (
              <Chip
                key={t.key}
                active={s.type === t.key}
                color={t.color}
                onClick={() => s.type !== t.key && onPatch({ type: t.key })}
              >
                {t.label}
              </Chip>
            ))}
          </div>
        </>
      ) : (
        <div className="flex flex-wrap items-center gap-2 mt-3">
          {s.unit && (
            <span className="text-xs font-extrabold tracking-wide px-2.5 py-1 rounded-lg bg-orange-500 text-white">
              <Highlight text={`UNIT ${s.unit}`} terms={terms} />
            </span>
          )}
          {s.area && (
            <Pill>
              <Highlight text={s.area} terms={terms} />
            </Pill>
          )}
          <Pill color={m.color}>{m.label}</Pill>
          {s.autoTagged && <Pill color="#f97316">✦ Auto-tagged</Pill>}
          {s.photos.length > 0 && (
            <Pill>
              {s.photos.length} photo{s.photos.length > 1 ? "s" : ""}
            </Pill>
          )}
        </div>
      )}

      {/* assignee */}
      <div className="flex items-center gap-2 mt-3">
        <span className="text-[10px] font-extrabold tracking-widest text-gray-500">
          ASSIGNED
        </span>
        <AssigneePicker
          value={s.assignedTo}
          users={users}
          onChange={onAssign}
          emptyLabel={
            log.assignedTo
              ? `${log.assignedTo.displayName} (log owner)`
              : "Unassigned"
          }
          clearLabel="Same as log owner"
        />
      </div>

      {/* deficiency status */}
      {s.type === "deficiency" && (
        <button
          onClick={() =>
            onPatch({ status: s.status === "open" ? "resolved" : "open" })
          }
          className={`mt-3 w-full text-left text-sm font-bold px-3 py-2 rounded-lg border ${
            s.status === "open"
              ? "border-red-500 bg-red-500/10 text-red-400"
              : "border-emerald-500 bg-emerald-500/10 text-emerald-400"
          }`}
        >
          {s.status === "open"
            ? "Open — click to mark resolved"
            : "Resolved — click to reopen"}
        </button>
      )}

      {/* tags */}
      {edit ? (
        <>
          <label className={`${labelCls} mt-3`}>TAGS</label>
          <div className="flex flex-wrap gap-2 mb-2">
            {(s.tags || []).map((t) => (
              <button
                key={t}
                onClick={() => removeTag(t)}
                className="text-xs font-semibold px-2.5 py-1 rounded-full bg-orange-500/20 text-orange-300 hover:bg-red-500/20 hover:text-red-300"
              >
                #{t} ✕
              </button>
            ))}
          </div>
          <input
            className={inputCls}
            value={tagDraft}
            placeholder="Add tag, press space or Enter"
            onChange={(e) => {
              const v = e.target.value;
              if (v.endsWith(",") || v.endsWith(" ")) addTag(v.slice(0, -1));
              else setTagDraft(v);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addTag(tagDraft);
              }
            }}
            onBlur={() => tagDraft.trim() && addTag(tagDraft)}
          />
        </>
      ) : (
        s.tags.length > 0 && (
          <div className="text-sm text-orange-400 font-semibold mt-3">
            <Highlight
              text={s.tags.map((t) => `#${t}`).join("  ")}
              terms={terms}
            />
          </div>
        )
      )}

      {/* notes */}
      {edit ? (
        <>
          <label className={`${labelCls} mt-3`}>NOTES</label>
          <textarea
            className={`${inputCls} min-h-[160px] leading-relaxed`}
            value={notes}
            placeholder={transcribing ? "Transcribing…" : "Notes"}
            onFocus={() => (typing.current = true)}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() => {
              typing.current = false;
              if ((s.notes || "") !== notes) onPatch({ notes });
            }}
          />
        </>
      ) : (
        <div className="mt-3 text-gray-100 whitespace-pre-wrap leading-relaxed">
          {notesTrim ? (
            <>
              <div className={long && !expanded[s._id] ? "line-clamp-6" : ""}>
                <Highlight text={notesTrim} terms={terms} />
              </div>
              {long && (
                <button
                  className="text-orange-400 text-sm font-semibold mt-1"
                  onClick={() =>
                    setExpanded((p) => ({ ...p, [s._id]: !p[s._id] }))
                  }
                >
                  {expanded[s._id] ? "Show less" : "Read more"}
                </button>
              )}
            </>
          ) : (
            <span className="italic text-gray-500">
              {transcribing ? "Transcribing…" : "No notes for this section."}
            </span>
          )}
        </div>
      )}

      {/* original transcript */}
      {transcript && transcript !== notesTrim && (
        <div className="mt-2">
          <button
            className="text-orange-400 text-sm font-semibold"
            onClick={() =>
              setExpanded((p) => ({
                ...p,
                [s._id + "t"]: !p[s._id + "t"],
              }))
            }
          >
            {expanded[s._id + "t"] ? "Hide" : "Show"} original voice transcript
          </button>
          {expanded[s._id + "t"] && (
            <div className="mt-2 p-3 rounded-lg border border-orange-400/30 bg-orange-400/5 text-sm text-gray-300 whitespace-pre-wrap">
              <Highlight text={transcript} terms={terms} />
            </div>
          )}
        </div>
      )}

      {/* photos — always clickable to enlarge; delete/add in edit mode */}
      {(s.photos.length > 0 || edit) && (
        <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mt-4">
          {s.photos.map((p) => (
            <div key={p._id} className="relative group">
              <AuthImage
                fileId={p.fileId}
                className="w-full aspect-square object-cover rounded-lg cursor-zoom-in hover:opacity-90"
                onClick={() => onViewPhoto(p.fileId)}
              />
              {edit && (
                <button
                  onClick={() => onDeletePhoto(p)}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 hover:bg-red-600 text-white text-xs opacity-0 group-hover:opacity-100"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
          {edit && (
            <>
              <button
                onClick={() => fileRef.current?.click()}
                className="aspect-square rounded-lg border border-dashed border-gray-500 text-gray-400 hover:text-orange-300 hover:border-orange-400 text-sm"
              >
                + Add
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  const files = [...e.target.files];
                  e.target.value = "";
                  if (files.length) onUpload(files);
                }}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ═════════════════════════ Combine: move sections in from other reports ═════════════════════════
function ImportSections({ logId, projectId, onMoved, onError }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [srcId, setSrcId] = useState("");
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState(false);

  const loadOthers = useCallback(async () => {
    try {
      const { data } = await http.get("/available-sections", {
        params: { projectId, excludeLogId: logId },
      });
      setLogs(data.logs || []);
    } catch (e) {
      onError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [projectId, logId]); // eslint-disable-line

  useEffect(() => {
    loadOthers();
  }, [loadOthers]);

  const src = logs.find((l) => l._id === srcId);
  const ids = Object.keys(picked).filter((k) => picked[k]);

  const toggleAll = () => {
    if (!src) return;
    const all = ids.length === src.sections.length;
    setPicked(
      all ? {} : Object.fromEntries(src.sections.map((x) => [x._id, true])),
    );
  };

  const move = async () => {
    if (!src || !ids.length) return;
    if (
      !window.confirm(
        `Move ${ids.length} section${ids.length === 1 ? "" : "s"} (with photos) from "${src.title}" into this report? They will be removed from the original.`,
      )
    )
      return;
    try {
      setBusy(true);
      await http.post(`/${logId}/sections/import`, {
        sourceLogId: srcId,
        sectionIds: ids,
      });
      setPicked({});
      await Promise.all([onMoved(), loadOthers()]);
    } catch (e) {
      onError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 bg-gray-800 border border-gray-700 rounded-xl p-4">
      {loading ? (
        <div className="text-sm text-gray-400">Loading reports…</div>
      ) : logs.length === 0 ? (
        <div className="text-sm text-gray-500">
          There are no other reports in this project to pull from.
        </div>
      ) : (
        <>
          <label className={labelCls}>FROM REPORT</label>
          <select
            value={srcId}
            onChange={(e) => {
              setSrcId(e.target.value);
              setPicked({});
            }}
            className={inputCls}
          >
            <option value="">Select a report…</option>
            {logs.map((l) => (
              <option key={l._id} value={l._id}>
                {l.title} — {new Date(l.logDate).toLocaleDateString("en-CA")} (
                {l.sections.length} section{l.sections.length === 1 ? "" : "s"})
              </option>
            ))}
          </select>

          {src && (
            <div className="mt-3">
              {src.sections.length === 0 ? (
                <div className="text-sm text-gray-500">
                  That report has no sections left.
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <button
                      onClick={toggleAll}
                      className="text-xs text-orange-400 font-semibold"
                    >
                      {ids.length === src.sections.length
                        ? "Deselect all"
                        : "Select all"}
                    </button>
                    <span className="text-xs text-gray-500">
                      {ids.length} selected
                    </span>
                  </div>
                  <div className="max-h-72 overflow-y-auto space-y-1.5 pr-1">
                    {src.sections.map((x) => {
                      const tm = typeMeta(x.type);
                      return (
                        <label
                          key={x._id}
                          className={`flex items-center gap-3 rounded-lg px-3 py-2 cursor-pointer border ${
                            picked[x._id]
                              ? "border-orange-400 bg-orange-400/10"
                              : "border-gray-700 hover:bg-gray-700/50"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={!!picked[x._id]}
                            onChange={(e) =>
                              setPicked((p) => ({
                                ...p,
                                [x._id]: e.target.checked,
                              }))
                            }
                            className="accent-orange-500"
                          />
                          <span
                            className="w-2 h-2 rounded-full shrink-0"
                            style={{ backgroundColor: tm.color }}
                          />
                          <span className="min-w-0 flex-1 text-sm text-gray-100 truncate">
                            {x.unit ? `Unit ${x.unit} · ` : ""}
                            {x.title || "Untitled"}
                            {x.area ? `  ·  ${x.area}` : ""}
                          </span>
                          {x.type === "deficiency" && (
                            <Pill
                              color={
                                x.status === "open" ? "#ef4444" : "#10b981"
                              }
                            >
                              {x.status}
                            </Pill>
                          )}
                          {x.photoCount > 0 && (
                            <span className="text-[11px] text-gray-500 shrink-0">
                              {x.photoCount} photos
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                  <button
                    onClick={move}
                    disabled={!ids.length || busy}
                    className="mt-3 px-4 py-2 rounded-md bg-orange-400 hover:bg-orange-500 text-white text-sm font-semibold disabled:opacity-40"
                  >
                    {busy
                      ? "Moving…"
                      : `Move ${ids.length || ""} section${ids.length === 1 ? "" : "s"} here`}
                  </button>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const Tile = ({ label, value, color = "#f3f4f6", active, onClick }) => (
  <button
    disabled={!onClick}
    onClick={onClick}
    className={`rounded-xl py-3 border text-center bg-gray-800 ${
      onClick ? "cursor-pointer hover:bg-gray-700" : "cursor-default"
    }`}
    style={{ borderColor: active ? color : "#374151" }}
  >
    <div className="text-2xl font-extrabold" style={{ color }}>
      {value}
    </div>
    <div className="text-[11px] font-bold tracking-wide text-gray-500 uppercase">
      {label}
    </div>
  </button>
);
